import {
  ServiceUnavailable,
  LoginRequired,
  UserNotActivated,
  TeamNotFound,
  TeamSubscriptionExpired,
  UnassignedTeamException
} from '@/exceptions';
import { RequestOptions } from '@/types/api';
import { ApiResponse } from '@/types/api';

const apiUrl = `${import.meta.env.VITE_FOOTBALLLOG_BACKEND_DOMAIN}/api`;

const fetchWithTimeout = async (url: string, options: RequestOptions, timeout = 30000) => {
  const controller = new AbortController();
  const id = setTimeout(() => controller.abort(), timeout);

  try {
    const response = await fetch(url, {
      ...options,
      signal: controller.signal
    });
    clearTimeout(id);
    return response;
  } catch (error) {
    clearTimeout(id);
    if (error instanceof Error && error.name === 'AbortError') {
      throw new Error('リクエストがタイムアウトしました');
    }

    throw error;
  }
};

const fetchWrapper = async <T>(path: string, options: RequestOptions): Promise<ApiResponse<T>> => {
  const response = await fetchWithTimeout(`${apiUrl}/${path}`, {
    ...options,
    // NOTE: Next.jsからDjangoへ通信する際に下記のオプション指定が必要
    // mode: 'cors'  CORSの許可を受けるために、ヘッダーにAccess-Control-Allow-Originを指定する。
    //credentials: 'include'  djagoで生成したCookieを含めて送信できるようにする。
    mode: 'cors',
    credentials: 'include'
  });

  // 2xx系以外
  if (!response.ok) {
    // メンテナンスモードの場合
    // NOTE: nginxから503送出しており、bodyがhtmlなのでresponse.json()するとエラーになる。
    // なので、503の場合は以下のエラーレスポンスをここで設定して呼び出し元に返却する。
    if (response.status === 503) {
      const maintenanceResponse: ApiResponse<T> = {
        ok: response.ok,
        status: response.status,
        code: ServiceUnavailable.CODE,
        detail: 'Service Unavailable'
      };
      return maintenanceResponse;
    }

    // Content-Typeを確認してJSONかどうかを判定
    const contentType = response.headers.get('Content-Type') || '';
    let errorData: Partial<ApiResponse<T>> = {};

    if (contentType.includes('application/json')) {
      try {
        errorData = (await response.json()) as ApiResponse<T>;
      } catch (jsonError) {
        console.error('JSONパースエラー:', jsonError);
      }
    } else {
      // JSONでない場合はレスポンスのテキストをそのまま使用
      const errorText = await response.text();
      console.error('非JSONレスポンス:', errorText);
      errorData = {
        code: 'UNKNOWN_ERROR',
        detail: 'サーバーから予期しないレスポンスが返されました'
      };
    }

    // エラー時のレスポンスを設定
    const errorResponse: ApiResponse<T> = {
      ok: response.ok,
      status: response.status,
      code: errorData.code || 'UNKNOWN_ERROR',
      detail: errorData.detail || '不明なエラーが発生しました'
    };

    // APIのエラー内容をログ出力
    console.log(errorResponse);

    return errorResponse;
  }

  // POSTなど、返却bodyがない場合を考慮して、text()メソッドを使用する
  const contentType = response.headers.get('Content-Type') || '';
  const resBody = await response.text();

  // レスポンスが空の場合、undefinedを返す
  let data: T | undefined = undefined;
  if (resBody) {
    if (contentType.includes('application/json')) {
      try {
        data = JSON.parse(resBody) as T;
      } catch (jsonError) {
        console.error('JSONパースエラー:', jsonError);
        throw new Error('サーバーから不正なJSONが返されました');
      }
    } else {
      // JSON以外のレスポンスはエラーとして扱う
      console.error('非JSONレスポンス:', resBody);
      throw new Error('サーバーから予期しないレスポンスが返されました');
    }
  }

  // レスポンスがJSON形式の場合、JSON.parse()を使用してオブジェクトに変換する
  const apiResponse: ApiResponse<T> = {
    ok: response.ok,
    status: response.status,
    data: data
  };

  return apiResponse;
};

export const getData = async <T>(path: string, options?: RequestOptions): Promise<ApiResponse<T>> => {
  options = {
    method: 'GET',
    ...options
  };
  return fetchWrapper<T>(path, options);
};

export const postData = async <T>(
  path: string,
  csrftoken: string,
  data?: object | FormData,
  options?: RequestOptions
): Promise<ApiResponse<T>> => {
  const isFormData = data instanceof FormData;
  const mergedOptions: RequestOptions = {
    method: 'POST',
    headers: {
      // NOTE: FormDataの場合はContent-Typeを指定しない（ブラウザが自動設定）
      ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
      'X-CSRFToken': csrftoken,
      ...options?.headers
    },
    // FormDataの場合はそのまま使用、それ以外はJSON.stringify
    body: isFormData ? data : JSON.stringify(data)
  };
  return fetchWrapper<T>(path, mergedOptions);
};

export const putData = async <T>(
  path: string,
  csrftoken: string,
  data?: object | FormData,
  options?: RequestOptions
): Promise<ApiResponse<T>> => {
  const isFormData = data instanceof FormData;
  const mergedOptions: RequestOptions = {
    method: 'PUT',
    headers: {
      // NOTE: FormDataの場合はContent-Typeを指定しない（ブラウザが自動設定）
      ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
      'X-CSRFToken': csrftoken,
      ...options?.headers
    },
    // FormDataの場合はそのまま使用、それ以外はJSON.stringify
    body: isFormData ? data : JSON.stringify(data)
  };
  return fetchWrapper<T>(path, mergedOptions);
};

export const deleteData = async <T>(
  path: string,
  csrftoken: string,
  options?: RequestOptions
): Promise<ApiResponse<T>> => {
  options = {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRFToken': csrftoken
    },
    ...options
  };
  return fetchWrapper<T>(path, options);
};

export const patchData = async <T>(
  path: string,
  csrftoken: string,
  data?: object | FormData,
  options?: RequestOptions
): Promise<ApiResponse<T>> => {
  const isFormData = data instanceof FormData;
  const mergedOptions: RequestOptions = {
    method: 'PATCH',
    headers: {
      // NOTE: FormDataの場合はContent-Typeを指定しない（ブラウザが自動設定）
      ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
      'X-CSRFToken': csrftoken,
      ...options?.headers
    },
    // FormDataの場合はそのまま使用、それ以外はJSON.stringify
    body: isFormData ? data : JSON.stringify(data)
  };
  return fetchWrapper<T>(path, mergedOptions);
};

// 共通エラーハンドリング関数
export function handleApiError(response: ApiResponse<unknown>) {
  // NOTE: 503: システムメンテナンス
  if (response.code === ServiceUnavailable.CODE) {
    throw new ServiceUnavailable('システムメンテナンス中です。');
  }

  // NOTE: 499: 要ログイン
  if (response.code === LoginRequired.CODE) {
    throw new LoginRequired('ログインが必要です');
  }

  // NOTE: 498: ユーザー情報がない(Activated=Falseなど)場合のカスタムエラーコード
  if (response.code === UserNotActivated.CODE) {
    throw new UserNotActivated('ユーザー情報がありません');
  }

  // NOTE: チームが存在しない
  if (response.code === TeamNotFound.CODE) {
    throw new TeamNotFound('チームが存在しません');
  }

  // NOTE: チームのサブスクリプション解約済み
  if (response.code === TeamSubscriptionExpired.CODE) {
    throw new TeamSubscriptionExpired('チームプラン解約済み');
  }

  // チーム未所属
  if (response.code === UnassignedTeamException.CODE) {
    throw new UnassignedTeamException('チームに所属していません');
  }

  throw new Error(response.detail);
}
